import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import TrustBadge from "@/components/TrustBadge";
import { CAUSE_COLORS, CAUSE_NAMES } from "@/lib/causes";
import { useAuth } from "@/contexts/AuthContext";
import { Globe, MapPin, Calendar, Users, ArrowLeft, CheckCircle2, Pencil, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [opp, setOpp] = useState(null);
  const [signups, setSignups] = useState([]);
  const [organizerTrust, setOrganizerTrust] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    api.get(`/opportunities/${id}`).then(({ data }) => {
      setOpp(data);
      if (data?.organizer_id) {
        api
          .get(`/users/${data.organizer_id}/profile`)
          .then(({ data: p }) => setOrganizerTrust(p.trust))
          .catch(() => setOrganizerTrust(null));
      }
    }).catch(() => setOpp(false));
    if (user && typeof user === "object") {
      api.get("/me/signups").then(({ data }) => setSignups(data)).catch(() => setSignups([]));
    } else {
      setSignups([]);
    }
  };

  useEffect(load, [id, user]);

  if (opp === null) return <Layout><div className="p-10 text-ink-soft">Loading…</div></Layout>;
  if (opp === false) return <Layout><div className="p-10">Not found.</div></Layout>;

  const isAuthed = user && typeof user === "object";
  const mySignup = signups.find((s) => s.opportunity_id === id);
  const isOrganizer = isAuthed && opp.organizer_id === user.id;
  const isFull = !!(opp.capacity && opp.capacity > 0 && opp.signups_count >= opp.capacity);

  const signup = async () => {
    setBusy(true);
    setMsg({ type: "", text: "" });
    try {
      await api.post("/opportunities/signup", { opportunity_id: id });
      setMsg({ type: "ok", text: "You're signed up! Show up, then mark complete." });
      load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const markComplete = async () => {
    if (!mySignup) return;
    setBusy(true);
    try {
      await api.post("/opportunities/mark-complete", { opportunity_id: mySignup.id });
      setMsg({ type: "ok", text: "Marked complete. Awaiting organizer verification." });
      load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  const cancelOpportunity = async () => {
    setCancelling(true);
    try {
      const { data } = await api.delete(`/opportunities/${id}`);
      toast.success(
        data.notified > 0
          ? `Opportunity cancelled. ${data.notified} volunteer(s) notified.`
          : "Opportunity cancelled."
      );
      nav("/organize", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setCancelling(false);
      setConfirmCancel(false);
    }
  };

  return (
    <Layout>
      <div data-testid="opportunity-detail-page" className="max-w-4xl mx-auto px-5 md:px-8 py-12">
        <button onClick={() => nav(-1)} className="text-sm text-ink-soft inline-flex items-center gap-1 mb-6" data-testid="opp-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="card-soft p-8 md:p-12">
          {opp.active === false && (
            <div
              data-testid="opp-cancelled-banner"
              className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">This opportunity has been cancelled by the organizer.</div>
                {opp.cancelled_at && (
                  <div className="text-xs mt-0.5 opacity-80">
                    Cancelled {new Date(opp.cancelled_at).toLocaleDateString()}. Any signed-up volunteers were notified — no Time Hours were charged.
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold text-white"
              style={{ background: CAUSE_COLORS[opp.cause] }}
              data-testid="opp-detail-cause"
            >
              {CAUSE_NAMES[opp.cause]}
            </span>
            <TimeBadge hours={opp.hours} size="lg" testId="opp-detail-hours" />
          </div>

          <h1 data-testid="opp-detail-title" className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-tight">
            {opp.title}
          </h1>

          <div className="mt-6 flex flex-wrap gap-4 text-sm text-ink-soft">
            {opp.mode === "online" ? (
              <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4" /> Online</span>
            ) : (
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {opp.location || "Location TBD"}</span>
            )}
            {opp.is_ongoing ? (
              <span className="inline-flex items-center gap-1.5 text-[var(--cm-primary)] font-semibold" data-testid="opp-detail-ongoing">
                <Calendar className="h-4 w-4" /> Ongoing
              </span>
            ) : (
              opp.date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {opp.date}</span>
            )}
            <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> {opp.signups_count} signed up</span>
          </div>

          <p className="mt-8 text-lg leading-relaxed whitespace-pre-line" data-testid="opp-detail-description">
            {opp.description}
          </p>

          <div className="mt-8 pt-6 border-t border-ink-soft text-sm text-ink-soft flex items-center gap-3 flex-wrap">
            <span>Organized by <span className="font-semibold text-ink">{opp.organizer_name}</span></span>
            {organizerTrust && <TrustBadge trust={organizerTrust} size="sm" testId="organizer-trust-badge" />}
          </div>

          {msg.text && (
            <div
              data-testid="opp-detail-message"
              className={`mt-6 text-sm px-4 py-3 rounded-lg ${
                msg.type === "ok" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
              }`}
            >
              {msg.text}
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {!isAuthed ? (
              <Link
                to="/login"
                state={{ from: { pathname: `/opportunities/${id}` } }}
                data-testid="opp-detail-signin-cta"
                className="btn-primary inline-flex items-center gap-2"
              >
                Sign in to volunteer · earn {opp.hours} hrs
              </Link>
            ) : isOrganizer ? (
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/organize" className="btn-secondary" data-testid="opp-detail-manage-btn">
                  Manage signups
                </Link>
                <Link
                  to={`/opportunities/${opp.id}/edit`}
                  className="btn-outline text-sm inline-flex items-center gap-1.5"
                  data-testid="opp-detail-edit-btn"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
                {opp.active !== false && (
                  <button
                    data-testid="opp-detail-cancel-btn"
                    onClick={() => setConfirmCancel(true)}
                    className="text-sm text-red-600 hover:text-red-700 inline-flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity"
                    title="Cancel this opportunity"
                  >
                    <XCircle className="h-3.5 w-3.5" /> Cancel opportunity
                  </button>
                )}
              </div>
            ) : mySignup ? (
              mySignup.status === "signed_up" ? (
                <button data-testid="opp-detail-mark-complete-btn" onClick={markComplete} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                  <CheckCircle2 className="h-4 w-4" /> {busy ? "…" : "I completed this"}
                </button>
              ) : mySignup.status === "completed_pending" ? (
                <span className="pill bg-amber-50 text-amber-800 border-amber-200" data-testid="opp-detail-pending-label">
                  Awaiting organizer verification
                </span>
              ) : mySignup.status === "credited" ? (
                <span className="pill bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="opp-detail-credited-label">
                  Credited — {mySignup.hours_credited} hours added
                </span>
              ) : (
                <span className="pill bg-red-50 text-red-700 border-red-200" data-testid="opp-detail-rejected-label">
                  Not verified
                </span>
              )
            ) : (
              <button
                data-testid="opp-detail-signup-btn"
                onClick={signup}
                disabled={busy || isFull}
                className="btn-primary disabled:opacity-50"
              >
                {isFull ? "Fully booked" : busy ? "Signing you up…" : `Sign up · earn ${opp.hours} hrs`}
              </button>
            )}
          </div>
        </div>

        {confirmCancel && (
          <div
            data-testid="opp-cancel-modal"
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !cancelling && setConfirmCancel(false)}
          >
            <div className="bg-surface rounded-2xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
              <div className="h-12 w-12 rounded-full bg-red-100 grid place-items-center mb-4">
                <XCircle className="h-6 w-6 text-red-700" />
              </div>
              <h2 className="font-display font-bold text-2xl">Cancel this opportunity?</h2>
              <p className="text-ink-soft mt-3 leading-relaxed text-sm">
                Any signed-up volunteers will be notified. Credited shifts from this opportunity keep their Time Hours — nothing is clawed back.
              </p>
              <p className="text-xs text-red-700 mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                This hides the opportunity from the public board. You cannot un-cancel it later.
              </p>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  data-testid="opp-cancel-modal-back"
                  onClick={() => setConfirmCancel(false)}
                  disabled={cancelling}
                  className="btn-outline text-sm disabled:opacity-60"
                >
                  Keep it live
                </button>
                <button
                  data-testid="opp-cancel-modal-confirm"
                  onClick={cancelOpportunity}
                  disabled={cancelling}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ background: "#c8322d" }}
                >
                  <XCircle className="h-4 w-4" /> {cancelling ? "Cancelling…" : "Yes, cancel it"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
